import React, { useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router';
import {
    Card,
    Tabs,
    Typography,
    Alert,
    Space,
    Skeleton
} from 'antd';
import {
    AppstoreOutlined,
    ThunderboltOutlined,
    DropboxOutlined,
    FireOutlined
} from '@ant-design/icons';
import { useOne } from "@refinedev/core";
import {
    electricityColumns,
    allColumns,
    waterColumns,
    heatColumns
} from '../../components/meters/columns';
import { MeterReadingsView } from '../../components/meters/MeterReadingsView';

const { Title } = Typography;

// Media type definitions
type MediaType = 'electricity' | 'all' | 'water' | 'heat';

interface RouteParams extends Record<string, string | undefined> {
    concentratorID: string;
    media: MediaType;
    viewMode?: 'current' | 'historical';
}


// Main meters component
export const Meters: React.FC = () => {
    const { concentratorID, media, viewMode } = useParams<RouteParams>();
    const navigate = useNavigate();
    const location = useLocation();

    // Fetch concentrator details to get the name
    const { result: concentratorData, query: {
        isLoading: concentratorLoading
    } } = useOne({
        resource: 'concentrators',
        id: concentratorID,
    });


    // Handle URL validation and correction - run when params change but be selective about redirects
    useEffect(() => {
        if (!concentratorID) return;
        const searchSuffix = location.search || "";
        
        // Only redirect if we have a valid media but no viewMode
        if (media && ['electricity', 'all', 'water', 'heat'].includes(media) && !viewMode) {
            navigate(`/concentrators/${concentratorID}/meters/${media}/current${searchSuffix}`, { replace: true });
            return; // Exit early to prevent multiple redirects
        }
        
        // Only redirect if we have invalid media
        if (media && !['electricity', 'all', 'water', 'heat'].includes(media)) {
            const defaultViewMode = viewMode || 'current';
            navigate(`/concentrators/${concentratorID}/meters/electricity/${defaultViewMode}${searchSuffix}`, { replace: true });
            return; // Exit early to prevent multiple redirects
        }
        
        // Only redirect if no media at all
        if (!media && concentratorID) {
            const defaultViewMode = viewMode || 'current';
            navigate(`/concentrators/${concentratorID}/meters/electricity/${defaultViewMode}${searchSuffix}`, { replace: true });
            return; // Exit early to prevent multiple redirects
        }
    }, [concentratorID, location.search, media, navigate, viewMode]);

    // Handle tab change
    const handleTabChange = (activeKey: string) => {
        const newValue = activeKey as MediaType;
        
        // Preserve current viewMode and query state when switching tabs
        const currentViewMode = viewMode || 'current';

        const searchSuffix = location.search || "";
        const newUrl = `/concentrators/${concentratorID}/meters/${newValue}/${currentViewMode}${searchSuffix}`;
        navigate(newUrl, { replace: true });
    };

    // Render error if concentratorID is missing
    if (!concentratorID) {
        return (
            <div style={{ maxWidth: 800, margin: '0 auto', padding: 24 }}>
                <Alert
                    message="Error"
                    description="Invalid concentrator ID. Please check the URL."
                    type="error"
                    showIcon
                />
            </div>
        );
    }

    // Tab configuration - only render the active tab to prevent unnecessary API calls
    const getTabContent = (mediaType: MediaType) => {
        const columnsMap = {
            electricity: electricityColumns(concentratorID),
            all: allColumns(),
            water: waterColumns(concentratorID),
            heat: heatColumns(concentratorID),
        };

        // Only render if this is the current media from URL
        if (media !== mediaType) {
            return null;
        }

        return (
            <MeterReadingsView
                concentratorID={concentratorID}
                media={mediaType}
                columns={columnsMap[mediaType]}
                concentratorName={concentratorData?.name}
            />
        );
    };

    const tabs = [
        {
            key: 'electricity',
            label: (
                <Space>
                    <ThunderboltOutlined />
                    Electricity
                </Space>
            ),
            children: getTabContent('electricity')
        },
        {
            key: 'water',
            label: (
                <Space>
                    <DropboxOutlined />
                    Water
                </Space>
            ),
            children: getTabContent('water')
        },
        {
            key: 'heat',
            label: (
                <Space>
                    <FireOutlined />
                    Heat
                </Space>
            ),
            children: getTabContent('heat')
        },
        {
            key: 'all',
            label: (
                <Space>
                    <AppstoreOutlined />
                    All
                </Space>
            ),
            children: getTabContent('all')
        }
    ];

    return (
        <Space direction="vertical" size="large" style={{ width: '100%' }}>
            {/* Page Header */}
            {concentratorLoading ? (
                <Skeleton.Input active style={{ width: 300, height: 32 }} />
            ) : (
                <Title level={2}>
                    Meters - {concentratorData?.name || `Concentrator ${concentratorID}`}
                </Title>
            )}
            {/* Tabs Navigation */}
            <Card>
                <Tabs
                    activeKey={media || 'electricity'}
                    onChange={handleTabChange}
                    items={tabs}
                    size="large"
                />
            </Card>
        </Space>
    );
};
